import React, { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils/cn";
import { humanizeLabel } from "@/lib/utils/humanize";

// ─── Helpers ──────────────────────────────────────────────────────
function alignClass(align: "left" | "center" | "right" = "left") {
  if (align === "center") return "text-center";
  if (align === "right") return "text-right";
  return "text-left";
}

// Filter values are matched case-insensitively; labels are title-cased so
// every option reads the same way regardless of how the data was stored.
const filterKey = (raw: string) => raw.trim().toLowerCase();
const filterLabel = (raw: string) =>
  humanizeLabel(raw).replace(
    /(^|[\s(\-/])([a-z])/g,
    (_, sep, ch) => sep + ch.toUpperCase(),
  );

// ─── Types ────────────────────────────────────────────────────────
export interface ColumnDef<T> {
  key: string;
  header: React.ReactNode;
  render: (row: T, index: number) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  align?: "left" | "center" | "right";
  /** Pins the column to the right edge on sm+. Several pinned columns stack from the right in render order. */
  pinRight?: boolean;
  /** Set false to hide the header filter for this column. */
  filterable?: boolean;
  /** Text used for header filtering and sorting. Defaults to row[key] when it's a primitive. */
  // Return an array to make each item a separate filter option (e.g. a player's teams).
  filterValue?: (
    row: T,
  ) => string | number | boolean | null | undefined | string[];
}

export interface TableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  getRowKey: (row: T) => string;
  renderSubRow?: (row: T) => React.ReactNode;
  enableSort?: boolean;
  /** Shows a filter button in each header. Default true. */
  enableColumnFilters?: boolean;
  enableRowSelection?: boolean;
  onRowSelectionChange?: (selected: T[]) => void;
  loading?: boolean;
  emptyMessage?: string;
  caption?: string;
  stickyHeader?: boolean;
  /** Uses fixed column widths so nested sub-row tables can line up with these columns. */
  fixedLayout?: boolean;
  className?: string;
}

export interface TableAction<T> {
  label: string;
  onClick: (row: T) => void;
  variant?: "default" | "danger";
  icon?: React.ReactNode;
}

export interface TableSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

// ─── TableSearch ──────────────────────────────────────────────────
export const TableSearch: React.FC<TableSearchProps> = ({
  value,
  onChange,
  placeholder = "Search…",
  className,
}) => (
  <div className={cn("relative w-[220px]", className)}>
    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
        <path
          fillRule="evenodd"
          d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
          clipRule="evenodd"
        />
      </svg>
    </span>
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        "h-9 w-full appearance-none rounded-xl border border-neutral-200 bg-white pl-9 pr-3 text-sm text-neutral-800 outline-none",
        "transition-colors placeholder:text-neutral-400",
        "focus:ring-[var(--sidebar-bg)]/20 focus:border-[var(--sidebar-bg)] focus:ring-2",
        "dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100",
      )}
    />
  </div>
);

// ─── RowActionsMenu ───────────────────────────────────────────────
export function RowActionsMenu<T>({
  row,
  actions,
  buttonClassName,
}: {
  row: T;
  actions: TableAction<T>[];
  buttonClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);

  useEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    const compute = () => {
      const btn = buttonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const menuMinWidth = 140;
      const leftUnclamped = rect.right - menuMinWidth;
      const left = Math.min(
        Math.max(leftUnclamped, 8),
        (window.innerWidth || document.documentElement.clientWidth) -
          (menuMinWidth + 8),
      );
      const top = rect.bottom + 4 + window.scrollY;
      setMenuStyle({
        position: "fixed",
        top: top,
        left: left + window.scrollX,
        minWidth: menuMinWidth,
        zIndex: 1100,
      });
    };

    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open]);

  const menu = open ? (
    <>
      <button
        type="button"
        aria-label="Close menu"
        onClick={() => setOpen(false)}
        style={{ position: "fixed", inset: 0, zIndex: 1099 }}
        className="cursor-default"
      />
      <div
        style={menuStyle ?? undefined}
        className="overflow-hidden rounded-xl border border-neutral-200 bg-white py-1 shadow-modal dark:border-neutral-700 dark:bg-neutral-900"
      >
        {actions.map((a, i) => {
          const danger = a.variant === "danger";
          return (
            <button
              key={i}
              type="button"
              onClick={() => {
                a.onClick(row);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
                danger
                  ? "text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/20"
                  : "text-neutral-700 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-800",
              )}
            >
              {a.icon && <span className="h-4 w-4 shrink-0">{a.icon}</span>}
              {a.label}
            </button>
          );
        })}
      </div>
    </>
  ) : null;

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Row actions"
        className={cn(
          "flex h-7 w-7 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-700 dark:hover:text-neutral-200",
          buttonClassName,
        )}
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
          <path d="M10 3a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM10 8.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM11.5 15.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0Z" />
        </svg>
      </button>
      {createPortal(menu, document.body)}
    </div>
  );
}

// ─── HeaderFilterMenu ─────────────────────────────────────────────
function HeaderFilterMenu({
  rect,
  options,
  selected,
  onChange,
  onClose,
}: {
  rect: DOMRect;
  options: { value: string; label: string; count: number }[];
  selected: string[];
  onChange: (values: string[]) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onClose);
    window.addEventListener("scroll", onClose, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  const menuWidth = 220;
  const left = Math.min(
    Math.max(rect.left, 8),
    window.innerWidth - menuWidth - 8,
  );

  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );

  return createPortal(
    <>
      <button
        type="button"
        aria-label="Close filter"
        onClick={onClose}
        style={{ position: "fixed", inset: 0, zIndex: 1099 }}
        className="cursor-default"
      />
      <div
        style={{
          position: "fixed",
          top: rect.bottom + 4,
          left,
          width: menuWidth,
          zIndex: 1100,
        }}
        className="rounded-xl border border-neutral-200 bg-white p-2 shadow-modal dark:border-neutral-700 dark:bg-neutral-900"
      >
        <div className="flex items-center justify-between px-1 pb-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
            Filter
          </span>
          <button
            type="button"
            onClick={() => onChange([])}
            disabled={selected.length === 0}
            className="text-xs font-medium text-[var(--sidebar-bg)] disabled:text-neutral-300"
          >
            Clear
          </button>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {options.length === 0 && (
            <p className="px-2 py-1.5 text-sm text-neutral-400">No values</p>
          )}
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-normal normal-case tracking-normal text-neutral-700 hover:bg-neutral-50 dark:text-neutral-200 dark:hover:bg-neutral-800"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                className="h-3.5 w-3.5 cursor-pointer rounded accent-[var(--sidebar-bg)]"
              />
              <span>
                {opt.label}{" "}
                <span className="text-neutral-400">({opt.count})</span>
              </span>
            </label>
          ))}
        </div>
      </div>
    </>,
    document.body,
  );
}

// ─── Table ────────────────────────────────────────────────────────
export function Table<T>({
  data,
  columns,
  getRowKey,
  renderSubRow,
  enableSort = true,
  enableColumnFilters = true,
  enableRowSelection = false,
  onRowSelectionChange,
  loading = false,
  emptyMessage = "No records found.",
  caption,
  stickyHeader = false,
  fixedLayout = false,
  className,
}: TableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // ── Column order / width ───────────────────────────────────────
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    columns.map((c) => c.key),
  );
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropKey, setDropKey] = useState<string | null>(null);
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  // Selected values per column; a missing or empty list means no filter.
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    {},
  );
  const [filterMenu, setFilterMenu] = useState<{
    key: string;
    rect: DOMRect;
  } | null>(null);
  const hasCustomWidths = Object.keys(colWidths).length > 0;
  // Right-pinned header cells, keyed by column, measured so each one sits flush against the next.
  const pinnedThRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const [pinRightOffsets, setPinRightOffsets] = useState<Record<string, number>>({});

  // Keep the user's order for known columns; append any new ones and drop removed ones.
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const orderedColumns = [
    ...columnOrder.filter((k) => byKey.has(k)),
    ...columns.map((c) => c.key).filter((k) => !columnOrder.includes(k)),
  ].map((k) => byKey.get(k)!);

  useEffect(() => () => resizeCleanupRef.current?.(), []);

  // Stack right-pinned columns: each sits to the right of the pinned columns that follow it.
  const pinnedKeysSignature = orderedColumns
    .filter((c) => c.pinRight)
    .map((c) => c.key)
    .join("|");
  useLayoutEffect(() => {
    const keys = pinnedKeysSignature ? pinnedKeysSignature.split("|") : [];
    const next: Record<string, number> = {};
    let offset = 0;
    for (let i = keys.length - 1; i >= 0; i--) {
      const key = keys[i];
      if (!key) continue;
      next[key] = offset;
      offset += pinnedThRefs.current[key]?.offsetWidth ?? 0;
    }
    setPinRightOffsets(next);
  }, [pinnedKeysSignature, colWidths]);

  // ── Sticky column logic ────────────────────────────────────────
  //
  // On sm+ (≥640px): the first 2 rendered columns are position:sticky.
  // On mobile (<640px): all columns scroll normally (no sticky applied).
  //
  // Column positions (0-indexed, in render order):
  //   0 → checkbox (if enableRowSelection) OR expand (if renderSubRow) OR first data col
  //   1 → expand / first data col / second data col
  //
  // The checkbox and expand columns are always w-10 (40px), so we know
  // their widths without measuring. Only when both of the first two sticky
  // columns are data columns do we need to measure col 0's rendered width.
  //
  // `col1Left` = the `left` CSS value for the second sticky column.
  const firstColThRef = useRef<HTMLTableCellElement>(null);
  const [col1Left, setCol1Left] = useState<number>(() => {
    // At least one utility column (40px) comes before the second sticky col
    // in the most common configurations. This is the correct default for
    // all cases EXCEPT when both sticky cols are data cols (no checkbox,
    // no subrow) — which useEffect corrects after the first render.
    return enableRowSelection || renderSubRow ? 40 : 0;
  });

  useEffect(() => {
    // Only needs to measure when col 0 is a data column (i.e. no utility
    // columns precede it), because we can't know its width at render time.
    if (!enableRowSelection && !renderSubRow && firstColThRef.current) {
      setCol1Left(firstColThRef.current.offsetWidth);
    }
  }, [enableRowSelection, renderSubRow, columns, columnOrder, colWidths]);

  // Returns the className additions for a sticky cell at `pos` (0 or 1).
  // `bg` should be the cell's base background class (differs between
  // thead and tbody).
  const stickyClass = (pos: number, bg: string) =>
    pos < 2
      ? cn(
          // sm+ only — mobile scrolls freely
          "sm:sticky sm:z-[1]",
          // background prevents content "bleeding" through during scroll
          bg,
          // right border on the last sticky col to mark the scroll boundary
          pos === 1 &&
            "sm:border-r sm:border-neutral-200 dark:sm:border-neutral-700",
        )
      : "";

  const stickyStyle = (pos: number): React.CSSProperties =>
    pos === 0 ? { left: 0 } : pos === 1 ? { left: col1Left } : {};

  // Right-pinned columns (e.g. Actions) stick to the right edge. Middle
  // columns scroll freely between the left and right pinned columns.
  const rightPinClass = (bg: string) =>
    cn(
      // z-[2] keeps the pinned column above left-sticky cells it scrolls past
      "sm:sticky sm:right-0 sm:z-[2]",
      bg,
      "sm:border-l sm:border-neutral-200 dark:sm:border-neutral-700",
    );

  const cellClass = (col: ColumnDef<T>, p: number, bg: string) =>
    col.pinRight ? rightPinClass(bg) : stickyClass(p, bg);

  const cellStyle = (col: ColumnDef<T>, p: number): React.CSSProperties =>
    col.pinRight ? { right: pinRightOffsets[col.key] ?? 0 } : stickyStyle(p);

  // ── Sort ───────────────────────────────────────────────────────
  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(key);
      setSortDir(1);
    }
  };

  // ── Resize / reorder ───────────────────────────────────────────
  const MIN_COL_WIDTH = 40;

  const startResize = (e: React.PointerEvent<HTMLSpanElement>, key: string) => {
    // Stop the header's sort click from firing on the handle.
    e.stopPropagation();
    const th = e.currentTarget.parentElement;
    if (!th) return;
    resizeCleanupRef.current?.();

    // Freeze every current column width so the others don't reflow.
    const snapshot: Record<string, number> = {};
    tableRef.current
      ?.querySelectorAll<HTMLTableCellElement>(
        "thead tr:first-child th[data-col]",
      )
      .forEach((el) => {
        const k = el.dataset.col;
        if (k) snapshot[k] = Math.round(el.getBoundingClientRect().width);
      });
    setColWidths((prev) => ({ ...snapshot, ...prev }));

    const startX = e.clientX;
    const startW = snapshot[key] ?? th.getBoundingClientRect().width;
    const onMove = (ev: PointerEvent) => {
      const w = Math.max(
        MIN_COL_WIDTH,
        Math.round(startW + ev.clientX - startX),
      );
      setColWidths((prev) => ({ ...prev, [key]: w }));
    };
    const cleanup = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", cleanup);
      window.removeEventListener("pointercancel", cleanup);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      resizeCleanupRef.current = null;
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", cleanup);
    window.addEventListener("pointercancel", cleanup);
    resizeCleanupRef.current = cleanup;
  };

  const moveColumn = (from: string, to: string) => {
    if (from === to) return;
    const keys = orderedColumns.map((c) => c.key);
    const fromIdx = keys.indexOf(from);
    keys.splice(fromIdx, 1);
    keys.splice(keys.indexOf(to), 0, from);
    setColumnOrder(keys);
  };

  const endDrag = () => {
    setDragKey(null);
    setDropKey(null);
  };

  // ── Expand / select ────────────────────────────────────────────
  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleSelect = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      onRowSelectionChange?.(data.filter((r) => next.has(getRowKey(r))));
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    const next = checked ? new Set(rows.map(getRowKey)) : new Set<string>();
    setSelected(next);
    onRowSelectionChange?.(checked ? [...rows] : []);
  };

  const colSpan =
    columns.length + (enableRowSelection ? 1 : 0) + (renderSubRow ? 1 : 0);

  // ── Filter / sort pipeline ─────────────────────────────────────
  const rawValue = (col: ColumnDef<T>, row: T): unknown =>
    col.filterValue
      ? col.filterValue(row)
      : (row as unknown as Record<string, unknown>)[col.key];

  const cellText = (col: ColumnDef<T>, row: T): string => {
    const raw = rawValue(col, row);
    if (Array.isArray(raw)) return raw.filter(Boolean).join(", ");
    if (raw == null || typeof raw === "object") return "";
    return String(raw);
  };

  // The individual values a cell contributes to filtering.
  const filterTexts = (col: ColumnDef<T>, row: T): string[] => {
    const raw = rawValue(col, row);
    if (Array.isArray(raw))
      return raw.map(String).filter((v) => v.trim() !== "");
    const text = cellText(col, row);
    return text === "" ? [] : [text];
  };

  // A column is filterable when it has a filterValue or primitive row values.
  const isFilterable = (col: ColumnDef<T>) =>
    col.filterable !== false &&
    !col.pinRight &&
    (!!col.filterValue ||
      data.some((row) => {
        const v = rawValue(col, row);
        return (
          typeof v === "string" ||
          typeof v === "number" ||
          typeof v === "boolean"
        );
      }));

  const colMap = new Map(columns.map((c) => [c.key, c]));
  const activeFilters = Object.entries(columnFilters).filter(
    ([, values]) => values.length > 0,
  );

  // A row passes when it matches every active filter, optionally skipping one column.
  const matchesFilters = (row: T, exceptKey?: string) =>
    activeFilters.every(([k, keys]) => {
      if (k === exceptKey) return true;
      const col = colMap.get(k);
      return (
        !col ||
        filterTexts(col, row).some((text) => keys.includes(filterKey(text)))
      );
    });

  // Distinct values for a column. Counts reflect rows that match the other filters.
  const optionsFor = (col: ColumnDef<T>) => {
    const groups = new Map<string, { label: string; count: number }>();
    data.forEach((row) => {
      if (!matchesFilters(row, col.key)) {
        // Still register the option so it can be seen and selected.
        filterTexts(col, row).forEach((raw) => {
          const key = filterKey(raw);
          if (!groups.has(key))
            groups.set(key, { label: filterLabel(raw), count: 0 });
        });
        return;
      }
      const seen = new Set<string>();
      filterTexts(col, row).forEach((raw) => {
        const key = filterKey(raw);
        if (seen.has(key)) return;
        seen.add(key);
        const group = groups.get(key) ?? { label: filterLabel(raw), count: 0 };
        group.count++;
        groups.set(key, group);
      });
    });
    return [...groups.entries()]
      .map(([value, g]) => ({ value, ...g }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { numeric: true }),
      );
  };

  let rows = data;
  if (activeFilters.length > 0) {
    rows = rows.filter((row) => matchesFilters(row));
  }
  const sortCol = enableSort && sortKey ? colMap.get(sortKey) : undefined;
  if (sortCol) {
    rows = [...rows].sort(
      (a, b) =>
        cellText(sortCol, a).localeCompare(cellText(sortCol, b), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * sortDir,
    );
  }

  return (
    <div className={cn("w-full", className)}>
      {caption && (
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
          {caption}
        </p>
      )}

      <div className="w-full overflow-x-auto rounded-xl border border-neutral-200 shadow-card dark:border-neutral-700 [&::-webkit-scrollbar-thumb]:rounded-sm [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-track]:bg-neutral-100 [&::-webkit-scrollbar]:h-1">
        <table
          ref={tableRef}
          className="w-full min-w-full border-collapse text-sm"
          style={
            hasCustomWidths || fixedLayout
              ? { tableLayout: "fixed" }
              : undefined
          }
        >
          {/* ── Head ──────────────────────────────────────────── */}
          <thead
            className={cn(
              "border-b border-neutral-200 bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800",
              stickyHeader && "sticky top-0 z-10",
            )}
          >
            <tr>
              {(() => {
                // Track the rendered column position across all th types
                let pos = 0;
                const headBg = "sm:bg-neutral-50 dark:sm:bg-neutral-800";

                return (
                  <>
                    {/* Checkbox column */}
                    {enableRowSelection &&
                      (() => {
                        const p = pos++;
                        return (
                          <th
                            key="__sel__"
                            ref={p === 0 ? firstColThRef : undefined}
                            className={cn(
                              "w-10 px-3.5 py-2.5 text-center",
                              stickyClass(p, headBg),
                            )}
                            style={stickyStyle(p)}
                          >
                            <input
                              type="checkbox"
                              checked={
                                selected.size === rows.length && rows.length > 0
                              }
                              onChange={(e) => toggleAll(e.target.checked)}
                              className="h-3.5 w-3.5 cursor-pointer rounded accent-[var(--sidebar-bg)]"
                            />
                          </th>
                        );
                      })()}

                    {/* Expand column */}
                    {renderSubRow &&
                      (() => {
                        const p = pos++;
                        return (
                          <th
                            key="__exp__"
                            ref={p === 0 ? firstColThRef : undefined}
                            className={cn(
                              "w-10 px-3.5 py-2.5",
                              stickyClass(p, headBg),
                            )}
                            style={stickyStyle(p)}
                          />
                        );
                      })()}

                    {/* Data columns */}
                    {orderedColumns.map((col) => {
                      const p = pos++;
                      const sortable = !!(enableSort && col.sortable !== false);
                      const active = sortKey === col.key;
                      // Pinned-right columns (Actions) keep their place.
                      const movable = !col.pinRight;
                      const isDropTarget =
                        dropKey === col.key && dragKey !== col.key;
                      const customWidth = colWidths[col.key];
                      return (
                        <th
                          key={col.key}
                          data-col={col.key}
                          ref={(el) => {
                            if (p === 0) firstColThRef.current = el;
                            if (col.pinRight) pinnedThRefs.current[col.key] = el;
                          }}
                          style={{
                            width: customWidth ?? col.width,
                            ...cellStyle(col, p),
                          }}
                          onClick={() => sortable && handleSort(col.key)}
                          draggable={movable}
                          onDragStart={(e) => {
                            if (!movable) return;
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/plain", col.key);
                            setDragKey(col.key);
                          }}
                          onDragOver={(e) => {
                            if (!dragKey || !movable) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                            if (dropKey !== col.key) setDropKey(col.key);
                          }}
                          onDrop={(e) => {
                            if (!dragKey || !movable) return;
                            e.preventDefault();
                            moveColumn(dragKey, col.key);
                            endDrag();
                          }}
                          onDragEnd={endDrag}
                          className={cn(
                            "relative select-none whitespace-nowrap px-3.5 py-2.5 text-xs font-semibold uppercase tracking-wide text-neutral-500 transition-colors dark:text-neutral-400",
                            hasCustomWidths && "overflow-hidden",
                            alignClass(col.align ?? "left"),
                            sortable
                              ? "cursor-pointer hover:text-[var(--sidebar-bg)]"
                              : "cursor-default",
                            movable && "cursor-grab",
                            dragKey === col.key && "opacity-50",
                            isDropTarget &&
                              "shadow-[inset_3px_0_0_var(--sidebar-bg)]",
                            cellClass(col, p, headBg),
                          )}
                        >
                          <span className="inline-flex max-w-full items-center gap-[3px]">
                            <span className="min-w-0 truncate">
                              {col.header}
                            </span>
                            {enableColumnFilters && isFilterable(col) && (
                              <button
                                type="button"
                                aria-label={`Filter ${typeof col.header === "string" ? col.header : col.key}`}
                                title="Filter"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFilterMenu(
                                    filterMenu?.key === col.key
                                      ? null
                                      : {
                                          key: col.key,
                                          rect: e.currentTarget.getBoundingClientRect(),
                                        },
                                  );
                                }}
                                className={cn(
                                  "shrink-0 rounded p-0.5 transition-colors hover:bg-neutral-200 dark:hover:bg-neutral-700",
                                  (columnFilters[col.key]?.length ?? 0) > 0
                                    ? "text-[var(--sidebar-bg)]"
                                    : "text-neutral-400",
                                )}
                              >
                                <svg
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  className="h-3 w-3"
                                >
                                  <path d="M2.628 1.601C5.028 1.206 7.49 1 10 1s4.973.206 7.372.601a.75.75 0 0 1 .628.74v2.288a2.25 2.25 0 0 1-.659 1.59l-4.682 4.683a2.25 2.25 0 0 0-.659 1.59v3.037c0 .684-.31 1.33-.844 1.757l-1.937 1.55A.75.75 0 0 1 8 18.25v-5.757a2.25 2.25 0 0 0-.659-1.591L2.659 6.22A2.25 2.25 0 0 1 2 4.629V2.34a.75.75 0 0 1 .628-.74Z" />
                                </svg>
                              </button>
                            )}
                            {sortable && (
                              <span
                                className={cn(
                                  "shrink-0 text-[9px]",
                                  active ? "opacity-100" : "opacity-30",
                                )}
                                style={
                                  active
                                    ? { color: "var(--sidebar-bg)" }
                                    : undefined
                                }
                              >
                                {active ? (sortDir === 1 ? "↑" : "↓") : "↕"}
                              </span>
                            )}
                          </span>
                          <span
                            role="separator"
                            aria-orientation="vertical"
                            title="Drag to resize"
                            onPointerDown={(e) => startResize(e, col.key)}
                            // Stop native header drag from starting on the handle.
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => e.stopPropagation()}
                            className="hover:bg-[var(--sidebar-bg)]/30 absolute right-0 top-0 h-full w-3 cursor-col-resize touch-none"
                          />
                        </th>
                      );
                    })}
                  </>
                );
              })()}
            </tr>
          </thead>

          {/* ── Body ──────────────────────────────────────────── */}
          <tbody className="bg-white dark:bg-neutral-900">
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2 text-sm text-neutral-500">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-200 border-t-[var(--sidebar-bg)]" />
                    <span>Loading…</span>
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={colSpan}
                  className="px-4 py-12 text-center text-sm text-neutral-500"
                >
                  {data.length > 0 && activeFilters.length > 0
                    ? "No records match the filters."
                    : emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const key = getRowKey(row);
                const isExp = expanded.has(key);
                const isSel = selected.has(key);

                // Background for sticky cells matches the row's current state
                const rowBg =
                  isSel || isExp
                    ? "sm:bg-[var(--sidebar-bg)]/5"
                    : "sm:bg-white dark:sm:bg-neutral-900";
                // On hover, row bg changes — sticky cells must follow.
                // We use group-hover on <tr> and target sticky tds.
                const hoverBg =
                  "sm:group-hover/row:bg-neutral-50 dark:sm:group-hover/row:bg-neutral-800";
                const bodyBg = cn(rowBg, hoverBg);

                return (
                  <Fragment key={key}>
                    <tr
                      className={cn(
                        "group/row border-b border-neutral-100 transition-colors last:border-b-0 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-800",
                        isSel || isExp
                          ? "bg-[var(--sidebar-bg)]/5"
                          : "bg-transparent",
                      )}
                    >
                      {(() => {
                        let pos = 0;

                        return (
                          <>
                            {/* Checkbox cell */}
                            {enableRowSelection &&
                              (() => {
                                const p = pos++;
                                return (
                                  <td
                                    key="__sel__"
                                    className={cn(
                                      "px-3.5 py-2.5 text-center align-middle",
                                      stickyClass(p, bodyBg),
                                    )}
                                    style={stickyStyle(p)}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSel}
                                      onChange={() => toggleSelect(key)}
                                      className="h-3.5 w-3.5 cursor-pointer rounded accent-[var(--sidebar-bg)]"
                                    />
                                  </td>
                                );
                              })()}

                            {/* Expand cell */}
                            {renderSubRow &&
                              (() => {
                                const p = pos++;
                                return (
                                  <td
                                    key="__exp__"
                                    className={cn(
                                      "px-3.5 py-2.5 text-center align-middle",
                                      stickyClass(p, bodyBg),
                                    )}
                                    style={stickyStyle(p)}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => toggleExpand(key)}
                                      aria-label={isExp ? "Collapse" : "Expand"}
                                      className={cn(
                                        "flex h-6 w-6 items-center justify-center rounded text-[10px] transition-all hover:bg-neutral-100",
                                        isExp
                                          ? "rotate-90 text-[var(--sidebar-bg)]"
                                          : "rotate-0 text-neutral-400",
                                      )}
                                    >
                                      ▶
                                    </button>
                                  </td>
                                );
                              })()}

                            {/* Data cells */}
                            {orderedColumns.map((col) => {
                              const p = pos++;
                              return (
                                <td
                                  key={col.key}
                                  className={cn(
                                    "whitespace-nowrap px-3.5 py-2.5 align-middle text-sm text-neutral-800 dark:text-neutral-100",
                                    alignClass(col.align ?? "left"),
                                    hasCustomWidths &&
                                      "overflow-hidden text-ellipsis",
                                    cellClass(col, p, bodyBg),
                                  )}
                                  style={cellStyle(col, p)}
                                >
                                  {col.render(row, idx)}
                                </td>
                              );
                            })}
                          </>
                        );
                      })()}
                    </tr>

                    {/* Sub-row */}
                    {isExp && renderSubRow && (
                      <tr className="border-l-[var(--sidebar-bg)]/40 bg-[var(--sidebar-bg)]/5 border-b border-l-[3px] border-b-neutral-100 dark:border-b-neutral-800">
                        <td
                          colSpan={
                            columns.length + (enableRowSelection ? 1 : 0) + 1
                          }
                          className="px-3.5 py-2.5 align-middle text-sm text-neutral-800 dark:text-neutral-100"
                        >
                          {renderSubRow(row)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {enableRowSelection && selected.size > 0 && (
        <p className="mt-1.5 text-xs text-neutral-500">
          {selected.size} row(s) selected
        </p>
      )}
      {filterMenu &&
        (() => {
          const col = colMap.get(filterMenu.key);
          if (!col) return null;
          return (
            <HeaderFilterMenu
              rect={filterMenu.rect}
              options={optionsFor(col)}
              selected={columnFilters[col.key] ?? []}
              onChange={(values) =>
                setColumnFilters((prev) => ({ ...prev, [col.key]: values }))
              }
              onClose={() => setFilterMenu(null)}
            />
          );
        })()}
    </div>
  );
}
